import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import { CommonModule } from '@angular/common';
import {
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    input,
    InputSignal,
    OnDestroy,
    Signal,
    signal,
    untracked,
    viewChild,
    WritableSignal,
} from '@angular/core';
import {
    IqEventBusBrokerHandler,
    IqEventBusBrokerService,
} from '@dat/iq-event-bus';
import { isEmpty, isEqual, isNil } from 'lodash';
import {
    Errors,
    MciChartPayload,
    MciChartPayloadSchema,
    MciData,
    noSelectionMciResponse,
    TimeFrame,
} from '../models';
import { IqMcChartsUtilsService } from '../state';

@Component({
    selector: 'iq-mc-charts-mci-prior',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './iq-mc-charts-mci.component.html',
    styleUrl: './iq-mc-charts-mci.component.scss',
})
export class IqMcChartsMciComponent implements OnDestroy {
    id: InputSignal<string> = input('');
    private _bus: IqEventBusBrokerService = inject(IqEventBusBrokerService);
    private _deregister: Record<string, IqEventBusBrokerHandler> = {};

    graphRef: Signal<ElementRef> = viewChild<ElementRef>('graph');
    mciData: WritableSignal<MciChartPayload> = signal(null);
    chartProps: WritableSignal<{
        root: am5.Root;
        chart: am5xy.XYChart;
    }> = signal(null);

    loaded: Signal<boolean> = computed(() => {
        const mciData = this.mciData();
        if (!!mciData) {
            return !isEmpty(mciData.data) && mciData.loaded;
        }
        if (isNil(mciData?.geoCode) || isEmpty(mciData?.geoCode)) {
            return false;
        }
        return false;
    }, { equal: isEqual});
    hasError: Signal<boolean> = computed(() => {
        const payload = this.mciData();
        return !this._validateControlSelections(payload);
    }, { equal: isEqual});

    errors: Signal<Errors> = computed(() => {
        const payload = this.mciData();
        if (!this._validateControlSelections(payload)) {
            return noSelectionMciResponse;
        }
        return {};
    }, { equal: isEqual});

    timeFrame: Signal<TimeFrame> = computed(() => {
        const mciData = this.mciData();
        return isNil(mciData) ? '' : this.mciData().timeframe;
    }, { equal: isEqual});

    mciScores: Signal<MciData> = computed(() => {
        return isNil(this.mciData()?.data) ? [] : this.mciData().data;
    }, { equal: isEqual});

    constructor(private _chartUtils: IqMcChartsUtilsService) {
        this._deregister = this._bus.registerMap({
            updateMcMciChart: {
                callback: (payload: MciChartPayload) => {
                    if (payload.id !== this.id()) return;
                    const validatePayload: any =
                        MciChartPayloadSchema.safeParse(payload);
                    if (!validatePayload.success) {
                        console.error('Invalid payload');
                        console.log(validatePayload?.error?.issues);
                        return;
                    }
                    this.mciData.set(payload);
                },
            },
        });

        effect(() => {
            const elemRef = this.graphRef();
            if (!elemRef) return;
            untracked(() => {
                const chartProps: { root: am5.Root; chart: am5xy.XYChart } =
                    this._chartUtils.renderGraph(elemRef);
                this.chartProps.set(chartProps);
            });
        });

        effect(async () => {
            const loaded = this.loaded();
            const chartLoaded = isNil(this.chartProps()) ? false : true;
            if (!loaded || this.hasError()) return;
            if (!chartLoaded) return;
                if (this._isPriorTimeFrame(this.timeFrame())) {
                    await this._chartUtils.renderMciPriorGraph(
                        this.chartProps().root,
                        this.chartProps().chart,
                        this.mciScores(),
                    );
                    return;
                }
                if (this._isForecast(this.timeFrame())) {
                    await this._chartUtils.renderMciForecastGraph(
                        this.chartProps().root,
                        this.chartProps().chart,
                        this.mciScores(),
                    );
                    return;
                }
        });
    }

    ngOnDestroy(): void {
        this._bus.unregisterMap(this._deregister);
    }

    private _validateControlSelections(payload: MciChartPayload): boolean {
        if (
            isNil(payload) ||
            isNil(payload.geoCode) ||
            !this._isValidTimeFrame(payload.timeframe) ||
            payload.hasError
        ) {
            return false;
        }
        return true;
    }

    private _isValidTimeFrame(timeFrame: TimeFrame): boolean {
        if (isNil(timeFrame)) {
            return false;
        }
        return !!(
            timeFrame === 'THIRTY_DAYS_BACK' ||
            timeFrame === 'EIGHT_DAYS_BACK' ||
            timeFrame === 'EIGHT_DAY_FORECAST'
        );
    }

    private _isPriorTimeFrame(timeFrame: TimeFrame): boolean {
        if (isNil(timeFrame)) {
            return false;
        }
        return !!(
            timeFrame === 'THIRTY_DAYS_BACK' || timeFrame === 'EIGHT_DAYS_BACK'
        );
    }

    private _isForecast(timeFrame: TimeFrame): boolean {
        if (isNil(timeFrame)) {
            return false;
        }
        return timeFrame === 'EIGHT_DAY_FORECAST';
    }
}
